import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { FileRestrictions, SelectEvent } from '@progress/kendo-angular-upload';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ViewableComponent } from '../../../../shared/components/interfaces/viewable.component';
import { ConfirmationModel } from '../../../../shared/model/confirmation.model';
import {
  UIActiveDialogService,
  UIDialogService
} from '../../../../shared/services/ui-dialog.service';
import { ProductService } from '../../../product/service/product.service';
import { AuthService } from '../../../security/service/auth.service';
import { AssetRoutingStates } from '../../asset-routing.module';
import { AssetHelper } from '../../asset.helper';
import { AssetService } from '../../service/asset.service';
import { AssetBaseComponent } from '../asset-base.component';
import { AssetPhotoUploadProgressDialog } from '../asset-photo-upload-progress-dialog/asset-photo-upload-progress-dialog.component';
import { AssetPhotoUploadModel } from '../model/asset-photo-upload.model';
import { AssetUIMessages } from '../model/asset-ui.messages';

export interface ActiveAssetMessages {
  ACTIVATE_MESSAGE: string;
  CONFIRMATION: string;
  SUCCESS: string;
  ERROR: string;
}

@Component({
  selector: 'asset-view',
  templateUrl:
    './console/app-js/modules/asset/components/view/asset-view.component.html'
})
export class AssetViewComponent extends AssetBaseComponent
  implements ViewableComponent {
  protected deleting = false;
  protected templateText = '';
  public disableAssetUpload: boolean;
  public uploadRestrictions: FileRestrictions = {
    allowedExtensions: [
      'png',
      'jpeg',
      'gif',
      'tiff',
      'jpg',
      'ppt',
      'pptx',
      'xlsx',
      'eml',
      'msg',
      'docx',
      'pdf',
      'heic'
    ]
  };
  public isUploadSuccess: boolean;
  private assetPhotoUploadModel: AssetPhotoUploadModel = new AssetPhotoUploadModel();
  selectedAttachmentsToDownload: string[];
  isMultidownloadEnable: boolean;
  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    assetService: AssetService,
    productService: ProductService,
    dialogService: UIDialogService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialogOpenService: UIDialogService,
    private dialogCloseService: UIActiveDialogService
  ) {
    super(
      progressService,
      toastr,
      authService,
      assetService,
      productService,
      dialogService
    );
    this.assetId = this.activatedRoute.snapshot.params['id'];
    this.disableAssetUpload = !this.canManageAssets();
    this.loadAsset();
  }

  /**
   * Load all the Asset information, calls to different nested endpoints.
   */
  private loadAsset(): void {
    this.progressRef.start();
    AssetHelper.getAssetDetails(this.assetId, this.assetService).subscribe(
      (res: any) => {
        this.assetModel = res;
        this.operations.loading = false;
        this.progressRef.complete();
        this.loadLinkedLinesOfService();
        this.loadAssetAttachments();
      },
      () => this.onAPIError(AssetUIMessages.LOAD_ASSET_ERROR)
    );
  }

  public selectEventHandler(event: SelectEvent): void {
    this.isUploadSuccess = false;
    this.assetPhotoUploadModel.assetId = this.assetId;
    this.assetPhotoUploadModel.selectEvent = event;
    this.dialogOpenService
      .open(AssetPhotoUploadProgressDialog, [
        {
          provide: 'selectEvent',
          useValue: this.assetPhotoUploadModel
        }
      ])
      .then(result => {
        this.isUploadSuccess = result;
        this.clearSuccessMessage();
        this.loadAssetAttachments();
      })
      .catch(error => {
        if (error) {
          this.onAPIError(AssetUIMessages.DEACTIVATE_FILE_UPLOAD_ERROR);
        }
      });
  }

  clearSuccessMessage() {
    setTimeout(() => {
      this.isUploadSuccess = false;
    }, 10000);
  }

  /**
   * On Edit button click, redirect to edit view.
   */
  onEdit(): void {
    this.router.navigate([
      `/console/${AssetRoutingStates.LIST.url}`,
      this.assetId,
      'edit'
    ]);
  }

  onCancel(): void {
    this.router.navigate([`/console/${AssetRoutingStates.LIST.url}`]);
  }

  onCopy(): void {
    // Not supported yet in this module.
  }

  onActivate(): void {
    let activeAssetMsg = this.getAssetMessages();
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            activeAssetMsg.ACTIVATE_MESSAGE,
            activeAssetMsg.CONFIRMATION
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.activeAsset();
        }
      })
      .catch(err => {
        if (err) {
          console.error(err);
        }
      });
  }

  onDeactivate(): void {
    // Not supported yet in this module.
  }

  /**
   * Deletes the current asset.
   */
  onDelete(): void {
    this.deleting = true;
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            AssetUIMessages.DEACTIVATE_ASSET,
            AssetUIMessages.DEACTIVATE_ASSET_CONFIRMATION.replace(
              '{0}',
              this.assetModel.serialNumber
            )
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.progressRef.start();
          this.assetService.deleteAsset(this.assetModel.id).subscribe(
            (res: any) => {
              this.deleting = false;
              this.onAPISuccess(AssetUIMessages.DEACTIVATE_ASSET_SUCCESS);
              this.loadAsset();
            },
            () => this.onAPIError(AssetUIMessages.DEACTIVATE_ASSETS_ERROR)
          );
        }
      })
      .catch(() => {
        this.deleting = false;
      });
  }

  protected activeAsset(): void {
    this.assetService.activateAsset(this.assetModel).subscribe(
      res => {
        if (res.responseCode === 200) {
          this.onAPISuccess(AssetUIMessages.ACTIVATE_ASSET_SUCCESS);
          this.loadAsset();
        } else {
          if (res.errorMessage) {
            this.onAPIError(res.errorMessage);
          }
        }
      },
      err => {
        console.error(err);
        this.onAPIError(AssetUIMessages.ACTIVATE_ASSET_ERROR);
      }
    );
  }

  private getAssetMessages(): ActiveAssetMessages {
    return {
      ACTIVATE_MESSAGE: AssetUIMessages.ACTIVATE_ASSET,
      CONFIRMATION: AssetUIMessages.ACTIVATE_ASSET_CONFIRMATION.replace(
        '{0}',
        this.assetModel.serialNumber
      ),
      SUCCESS: AssetUIMessages.ACTIVATE_ASSET_SUCCESS,
      ERROR: AssetUIMessages.ACTIVATE_ASSET_ERROR
    };
  }

  onDownloadSelected(): void {
    if (this.selectedAttachmentsToDownload == undefined) {
      return;
    }

    this.selectedAttachmentsToDownload.forEach(function(value) {
      window.open(value, '_blank');
    });
  }

  getSelectedAttachments(attachmentUrls): void {
    this.selectedAttachmentsToDownload = attachmentUrls;

    if (
      this.selectedAttachmentsToDownload &&
      this.selectedAttachmentsToDownload.length > 0
    ) {
      this.isMultidownloadEnable = true;
    } else {
      this.isMultidownloadEnable = false;
    }
  }

  onTabSelect(event: any): void {
    this.selectedAttachmentsToDownload = [];
    this.isMultidownloadEnable = false;
  }
}
